import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {
  LoginResponse,
  LoginSuccess,
  PermissionCode,
  SessionUser,
} from '@asyagrup/group-hub-contracts';
import {
  api,
  setAccessToken,
  setSessionLostHandler,
  oturumuGeriYukle,
  ApiError,
  isSessionEnded,
} from '@/lib/api/client';

/**
 * `unreachable` is the fourth state, and it exists because the other three
 * cannot tell the truth about a server that did not answer.
 *
 * "There is no session" and "I could not ask" are different facts, and this
 * boot path used to collapse them into the second one: any thrown error
 * cleared the session and dropped the person on the login screen with no
 * message. A refresh cookie that was perfectly valid then looked, to its
 * owner, like being thrown out — they retyped a password that was never
 * wrong. Production shows the shape: on 2026-08-17 one user logged in six
 * times, twice within six seconds, and no failed attempt was ever recorded
 * against her.
 *
 * It is not a rare window either. Every release recreates the web container,
 * and there were eleven releases on that single day.
 */
type Status = 'loading' | 'authenticated' | 'anonymous' | 'unreachable';

/**
 * Four attempts over about seven seconds, then say so.
 *
 * Long enough to cross a colour swap or a dropped Wi-Fi packet, short enough
 * that a person is not left watching a spinner that will never resolve. A
 * deploy takes longer than this, which is the point of the screen the fourth
 * failure leads to: it names the problem and offers the retry, rather than
 * pretending to be a login form.
 */
const BOOT_RETRY_DELAYS_MS = [500, 1000, 2000, 4000];

/**
 * Ceiling on ONE attempt, so the four delays above actually bound the wait.
 *
 * Six seconds is far past a healthy answer — this is one indexed lookup and a
 * signature — and well short of the browser's own patience, which is what a
 * half-open socket would otherwise spend.
 */
const BOOT_ATTEMPT_TIMEOUT_MS = 6000;

/** What a failed boot refresh permits the app to do next. */
export type BootFailure = 'signed-out' | 'unreachable' | 'retry';

/**
 * Reads a failed boot refresh, and is exported only so it can be tested.
 *
 * The classification is the whole of the boot half of DST-00042, and it is not
 * observable from outside: the component renders the same spinner while it
 * decides. Pulling the decision out of the loop is what lets the rule be
 * asserted directly instead of inferred from a screen.
 */
export function classifyBootFailure(err: unknown): BootFailure {
  // An HTTP RESPONSE came back, so the request reached something that speaks
  // for the server — and the refresh token may already be spent. It is
  // single-use and this endpoint is not idempotent: `refresh` commits
  // `consumedAt` before it writes its reply, so a reply lost after that commit
  // leaves the browser holding a token the server considers used. Presenting
  // it again is read as THEFT — the family is revoked, every other device is
  // signed out, and a critical security event is filed about an attack that
  // never happened. So an answer of any kind ends the retries.
  //
  // Which answer it was still decides the verdict, and only 401/403 are an
  // answer ABOUT THE SESSION. `isSessionEnded` holds that rule for both this
  // path and the in-session refresh, because a second copy would drift — and
  // drift here means signing people out for a rate limit or a route that had
  // not finished deploying.
  if (err instanceof ApiError) return isSessionEnded(err) ? 'signed-out' : 'unreachable';

  // A timeout is NOT a request that never landed. The ceiling above fires
  // after the bytes went out, so the server may have consumed the token and
  // simply been slow to say so — retrying would replay it and raise the same
  // false theft alarm the branch above exists to avoid. It is terminal for
  // exactly that reason, not because 6s is long.
  if (err instanceof DOMException && err.name === 'TimeoutError') return 'unreachable';

  // Nothing answered, and nothing was written: `fetch` rejected before a
  // response existed — connection refused while the web container is
  // recreated, or DNS. The request never reached the server, so the token is
  // untouched and the session's existence is still an open question. This is
  // the only failure a retry may follow.
  return 'retry';
}

interface SessionState {
  status: Status;
  user: SessionUser | null;
  /**
   * Typed `PermissionCode[]`, never `undefined`.
   *
   * asya.click's frontend guard reads `if (!user.permissions?.pageAccess) return true`
   * — "we don't know, so allow". That branch is unwritable here because the
   * type has no absent state: it is [] until the session resolves, and [] denies.
   */
  permissions: PermissionCode[];
  login: (email: string, password: string) => Promise<LoginResponse>;
  /**
   * Second leg of an MFA login.
   *
   * Separate from `login` because it carries no password: the first leg already
   * proved that, and `mfaToken` is a single-purpose five-minute credential. The
   * response shape is the same LoginSuccess, so the session is applied the same
   * way — there is one place where this app becomes authenticated.
   */
  completeMfa: (mfaToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: PermissionCode) => boolean;
  /** Re-reads the session after something changed the caller's own record. */
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  useEffect(() => {
    setSessionLostHandler(clear);
  }, [clear]);

  // On boot the access token is gone (it only ever lived in memory), so the
  // refresh cookie is the sole way back into an existing session.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          // `api.post` DEĞİL: açılış yenilemesi de sekmeler arası kilidin içinden
          // geçmeli. Doğrudan çağrıldığında birlikte açılan sekmeler aynı çerezi
          // aynı anda sunuyor ve sunucu bunu hırsızlık sayıp oturumun tamamını
          // iptal ediyordu.
          // Bounded per attempt, or the budget below bounds nothing: a socket
          // that connects and then goes quiet — the signature of a container
          // being recreated mid-request — hangs until the browser's own limit,
          // and the "about seven seconds" this loop promises becomes minutes of
          // spinner. An abort rejects with a DOMException rather than an
          // ApiError, so it lands in the retry branch, which is where it
          // belongs: nothing answered.
          const res = await oturumuGeriYukle<{ accessToken: string; user: SessionUser }>(
            AbortSignal.timeout(BOOT_ATTEMPT_TIMEOUT_MS),
          );
          if (cancelled) return;
          setAccessToken(res.accessToken);
          setUser(res.user);
          setStatus('authenticated');
          return;
        } catch (err) {
          if (cancelled) return;

          const verdict = classifyBootFailure(err);
          if (verdict === 'signed-out') {
            clear();
            return;
          }
          if (verdict === 'unreachable') {
            setStatus('unreachable');
            return;
          }

          const delay = BOOT_RETRY_DELAYS_MS[attempt];
          if (delay === undefined) {
            setStatus('unreachable');
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, delay));
          if (cancelled) return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clear]);

  const applySession = useCallback((res: LoginSuccess) => {
    setAccessToken(res.accessToken);
    setUser(res.user);
    setStatus('authenticated');
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResponse> => {
      const res = await api.post<LoginResponse>(
        '/auth/login',
        { email, password },
        {
          skipRefresh: true,
        },
      );
      if (res.status === 'ok') applySession(res);
      return res;
    },
    [applySession],
  );

  const completeMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const res = await api.post<LoginSuccess>(
        '/auth/mfa/verify',
        { mfaToken, code },
        { skipRefresh: true },
      );
      applySession(res);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { skipRefresh: true });
    } finally {
      clear();
    }
  }, [clear]);

  const permissions = useMemo(() => (user?.permissions ?? []) as PermissionCode[], [user]);

  const can = useCallback(
    (permission: PermissionCode) => permissions.includes(permission),
    [permissions],
  );

  const refresh = useCallback(async () => {
    try {
      setUser(await api.get<SessionUser>('/auth/session'));
    } catch {
      // A failure here means the session is gone; the client's 401 handling
      // has already cleared it.
    }
  }, []);

  const value = useMemo<SessionState>(
    () => ({ status, user, permissions, login, completeMfa, logout, can, refresh }),
    [status, user, permissions, login, completeMfa, logout, can, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession yalnızca SessionProvider içinde kullanılabilir.');
  return ctx;
}
